import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import BasicTable from "../../../Components/Table/Material/index.js";
import useApi from "../../../Hooks/useApi.js";
import { usePagination } from "../../../Hooks/usePagination.js";
import Modal from "../../../Components/Modal/index.js";
import SurveyCityListModal from "../../../Components/Modal/SurveyCityListModal.js";

import { useModal } from "../../../Hooks/useModal.js";
import { handleApiError } from "../../../Helpers/ErrorHandler.js";
import Common from "../../../Components/Layouts/Common.js";
import ButtonToolbar from "../../Shared/ButtonToolbar.js";
import useWindowWidth from "../../../Hooks/useWindowWidth.js";
import TextField from "../../../Components/UI/Inputs/TextField.js";
import DropDown from "../../../Components/UI/Inputs/DropDown.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark, faFilter } from "@fortawesome/free-solid-svg-icons";
import { sortRows, nextSortConfig } from "../../../Helpers/tableSort.js";

const STATUS_OPTIONS = [
  { name: "All", value: "" },
  { name: "Draft", value: "DRAFT" },
  { name: "Pending", value: "PENDING" },
  { name: "Completed", value: "COMPLETED" },
];

const ManageSurvey = () => {
  const { request } = useApi();
  const { t } = useTranslation();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [surveyData, setsurveyData] = useState();
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const paginateProps = usePagination();
  const { isOpen, closeModal, openModal } = useModal();

  const fetchSurveyList = async () => {
    try {
      const params = new URLSearchParams({
        offset: paginateProps.page,
        limit: paginateProps.rowsPerPage,
      });
      if (statusFilter) params.append("status", statusFilter);

      const response = await request(
        "GET",
        `/v1/survey/list?${params.toString()}`,
        {},
        "survey_service"
      );

      setsurveyData(
        response.content.map((el) => {
          return {
            ...el,
            assigned_cities: el.assignedCities,
            submitted_cities: el.submittedCities,
            assigned_date: el.startDate,
            end_date: el.endDate,
          };
        })
      );
      paginateProps.setTotalData(response.totalElements);
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };

  useEffect(() => {
    fetchSurveyList();
  }, [paginateProps.page, paginateProps.rowsPerPage, statusFilter]);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    paginateProps.setPage(0);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => nextSortConfig(prev, key));
  };

  const displayData = useMemo(() => {
    let rows = surveyData || [];
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      rows = rows.filter((row) => row.name?.toLowerCase().includes(term));
    }
    return sortRows(rows, sortConfig);
  }, [surveyData, searchTerm, sortConfig]);

  const columns = [
    {
      id: "name",
      label: "Survey Title" || t("Survey.title"),
      minWidth: 170,
      sortable: true,
    },
    {
      id: "assigned_cities",
      label: "#Assigned Cities",
      minWidth: 100,
      sortable: true,
    },
    {
      id: "submitted_cities",
      label: "#Submitted Cities",
      minWidth: 100,
      align: "left",
      sortable: true,
    },
    {
      id: "assigned_date",
      label: "Assignment Date",
      minWidth: 100,
      align: "left",
      sortable: true,
      // renderCell: (row) => {
      //   return new Date(row.assigned_date)?.toLocaleDateString();
      // },
    },
    {
      label: "End Date",
      id: "end_date",
      minWidth: 100,
      align: "left",
      sortable: true,
      // renderCell: (row) => {
      //   return new Date(row.end_date)?.toLocaleDateString();
      // },
    },
    {
      id: "action",
      label: "Action",
      minWidth: 150,
      align: "center",
      renderCell: (row) => (
        <ActionButtonPanel
          row={row}
          handleCityList={(e) => {
            e.stopPropagation();
            setSelectedSurvey({
              surveyId: row.surveyId,
              surveyName: row.name,
            });
            openModal();
          }}
        />
      ),
    },
  ];

  const toggleFilterVisibility = () => {
    setIsFilterVisible(!isFilterVisible);
  };

  const windowWidth = useWindowWidth();

  return (
    <div className=" bg-white  relative h-full">
      <Common headline={"Manage Survey"}>
        <ButtonToolbar
          title={"Add New Survey"}
          navigateURL={"/create-survey"}
        />

        <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faFilter} />}
            onClick={toggleFilterVisibility}
          >
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>
          {(searchTerm || statusFilter) && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FontAwesomeIcon icon={faXmark} />}
              onClick={() => {
                setSearchTerm("");
                handleStatusFilterChange("");
              }}
            >
              Reset Filters
            </Button>
          )}
        </div>

        <Collapse in={isFilterVisible}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-4">
            <TextField
              value={searchTerm}
              name="searchTerm"
              onValueChange={(_, value) => setSearchTerm(value)}
              icon={<FontAwesomeIcon icon={faSearch} />}
              placeholder="Search survey title (current page)"
              fullWidth
            />
            <DropDown
              setter={handleStatusFilterChange}
              placeholder="Select Status"
              value={statusFilter}
              options={STATUS_OPTIONS}
              fullWidth
            />
          </div>
        </Collapse>

        <BasicTable
          paginateProps={paginateProps}
          columns={columns}
          data={displayData}
          sortConfig={sortConfig}
          onSort={handleSort}
          maxHeight={15000}
        />

        <Modal
          isOpen={isOpen}
          onClose={closeModal}
          style={{ width: windowWidth >= 768 ? "70vw" : "90vw" }}
        >
          <SurveyCityListModal survey={selectedSurvey} />
        </Modal>
      </Common>
    </div>
  );
};

const ActionButtonPanel = ({ row, handleCityList }) => {

  return (
    <div className="inline-flex flex-col flex-wrap gap-2 justify-center text-xs">
      <Link
        to={"/view-survey-questionnaire"}
        state={{
          id: row.questionnaireId,
          viewType: "view",
        }}
      >
        <button
          className={`w-[130px] bg-[#1976d2] border border-gray-300  text-white rounded-md flex items-center justify-center p-1.5`}
        >
          View Mapped Questionnaire
        </button>
      </Link>

      <button
        onClick={handleCityList}
        className={`w-[130px] bg-[#1976d2] border border-gray-300  text-white rounded-md flex items-center justify-center p-1.5`}
      >
        City List
      </button>

      <Link
        to={"/survey-data-submission-city-list"}
        state={{
          surveyId: row.surveyId,
          surveyName: row.name,
          questionId: row.questionnaireId,
        }}
      >
        <button
          className={`w-[130px] bg-[#1976d2] border border-gray-300  text-white rounded-md flex items-center justify-center p-1.5`}
        >
          Data Submission
        </button>
      </Link>
    </div>
  );
};

export default ManageSurvey;
